<template>
  <Layout>
  <DsIslandsContainer>
    <DsIsland v-if="noAcceptableItems">
      <DsResultBanner
        type="neutral"
        size="small"
      >
        <template #title>
          <span v-html="translate('kid-list:title-noAcceptable')"></span>
        </template>
      </DsResultBanner>
    </DsIsland>
    <DsIsland>
      <DsTitle v-if="!noAcceptableItems" size="h1">
        <span v-html="translate('kid-list:title')" />
      </DsTitle>
      <template v-for="(option) in kidsList">
        <DsCell @click.native="onClick(option)" :key="option.iin">
          <template #default>
            <div class="body-500">
              {{ option.lastName }} {{ option.firstName }} {{ option.middleName }}
            </div>
            <div class="caption-500 text-text-secondary mt-spacing-350">
              <div
                v-if="option.status === 'MIN_AGE'"
                v-html="translate('kid-list:tip-1')"/>
              <div
                v-if="option.status === 'HAS_GOLD_WITH_REISSUE'"
                v-html="translate('kid-list:tip-2')"/>
              <div
                v-if="option.status === 'HAS_GOLD'"
                v-html="translate('kid-list:tip-2')"/>
              <div
                v-if="option.status === 'MAX_AGE'"
                v-html="translate('kid-list:tip-3')"/>
            </div>
          </template>
          <template #right>
            <DsIcon v-if="option.status === 'OK'" name="system/chevron-right-400" />
          </template>
        </DsCell>
      </template>
    </DsIsland>
    <DsIsland v-if="!noAcceptableItems && isGuideEnabled">
      <snippet
        v-if="isGuideEnabled"
        :lang="getLocalePlease"
        :title="translate('kid-list:guide-title')"
        code="KaspiGoldAgreementService"
        :contour="guideContour" />
    </DsIsland>
    <DsAlert :visible="alertRef.maxAge" layout="vertical">
      <template #title>
        <span v-html="translate('alert:max-age', alertRef.name)"/>
      </template>
      <DsButton type="ghost" size="large" @click="alertRef.maxAge = false">
        {{ translate('alert:clear') }}
      </DsButton>
    </DsAlert>
    <DsAlert :visible="alertRef.reissue" layout="vertical">
      <template #title>
        <span v-html="translate('alert:has-gold', alertRef.name)"/>
      </template>
      <DsButton type="ghost" size="large" @click="onClickReRelease">
        {{ translate('alert:re-release') }}
      </DsButton>
      <DsButton type="ghost" size="large" @click="alertRef.reissue = false">
        {{ translate('alert:cancel') }}
      </DsButton>
    </DsAlert>
    <DsAlert :visible="alertRef.hasGold" layout="vertical">
      <template #title>
        <span v-html="translate('alert:has-gold', alertRef.name)"/>
      </template>
      <DsButton type="ghost" size="large" @click="onClickReRelease" v-if="alertRef.getCard">
        {{ translate('alert:get-card') }}
      </DsButton>
      <DsButton type="ghost" size="large" @click="onClickReRelease" v-else>
        {{ translate('alert:re-release') }}
      </DsButton>
      <DsButton type="ghost" size="large" @click="alertRef.hasGold = false">
        {{ translate('alert:cancel') }}
      </DsButton>
    </DsAlert>
    <DsAlert :visible="alertRef.minAge" layout="vertical">
      <template #title>
        <span v-html="translate('alert:min-age', alertRef.name)"/>
      </template>
      <DsButton type="ghost" size="large" @click="alertRef.minAge = false">
        {{ translate('alert:clear') }}
      </DsButton>
    </DsAlert>
    <DsButtonBar sticky v-if="noAcceptableItems">
      <DsButton size="large" @click="goToMain">
        <span class="label-600-soft">
          {{translate('kid-list:btn-to-main')}}
        </span>
      </DsButton>
    </DsButtonBar>
  </DsIslandsContainer>
  </Layout>
</template>
<script setup>
import { sendAnalyticEvent } from '@/helpers/sendAnalyticEvent'
import { eventBus } from 'kaspi.frontend/src/modules/eventBus/eventBus'
import translate from "@/filters/translate.filter"
import {snippet} from '@guide/guide-snippet-vue2'
import "@guide/guide-snippet-vue2/styles"
import { DsTitle, DsIsland, DsIslandsContainer, DsCell, DsIcon, DsDivider, DsAlert, DsButtonBar , DsButton, DsResultBanner } from '@ds/vue2'
import { getLocale } from '@/modules/locale/locale'

import {onBeforeUnmount, onMounted, ref, computed} from 'vue'
import useStore from '@/composables/useStore'
import useHeader from '@/composables/useHeader'
import { getDeviceType } from '@/helpers/getDeviceType'
import Layout from '@/components/ui/Layout/Layout.vue'
const store = useStore()
const {setBackFn, hideBackBtn} = useHeader()
setBackFn(() => {
  store.dispatch('goldkidProcess/component', 'start')
})
const kidsList = ref([])
const getLocalePlease = computed(() => {
  return getLocale() === 'ru-RU' ? 'ru' : 'kz'
})
const guideContour = process.env.VUE_APP_GUIDE_CONTOUR
const noAcceptableItems = ref(false)
const alertRef = ref({
  minAge: false,
  hasGold: false,
  maxAge: false,
  reissue: false,
  name: '',
  getCard: false
})

const isGuideEnabled = computed(() => {
  return store.getters['goldkidProcess/isGuideTipsEnabled']
})
const isIos = () => {
  return getDeviceType() === 'ios'
}
onMounted(async () => {
  await dataStep()
  eventBus.$emit('kaspiLoading', false)
})

const dataStep = async () => {
  const response = await store.dispatch('goldkidProcess/dataStep')
  kidsList.value = response?.data?.data?.kidsList
  if (response?.data?.data?.noAcceptableItems) {
    noAcceptableItems.value = response?.data?.data?.noAcceptableItems
    hideBackBtn()
    sendAnalyticEvent('online_child_gold_rejected', {
      started_from: [store.getters['app/urlParams'].startedFrom],
      stage: ['start'],
      page_path: ['/bank/bpm/products/goldkids/goldkid'],
      reject_reason: [translate('kid-list:title-noAcceptable')],
      external_message_id: [store.getters['goldkidProcess/pId']],
      child_age: [kidsList.value?.map(item => item.age)]
    })
  } else {
    sendAnalyticEvent('online_child_gold_children_list', {
      started_from: [store.getters['app/urlParams'].startedFrom],
      stage: ['start'],
      page_path: ['/bank/bpm/products/goldkids/goldkid'],
      children_total: [kidsList.value.length],
      children_available: [kidsList.value?.filter(item => item.status === 'OK').length],
      external_message_id: [store.getters['goldkidProcess/pId']]
    })
  }
}
const onClickReRelease = () => {
  store.dispatch('goldkidProcess/component', 'reRelease')
}
const onClick = (option) => {
  const data = {
    meta: store.getters['goldkidProcess/meta'],
    data: {
      kidIin: option.iin?.split(' ').join('')
    }
  }
  switch (option.status) {
    case 'OK':
      store.dispatch('goldkidProcess/processStep', data).then((response) => {
        store.dispatch('goldkidProcess/component', response?.data?.data?.component)
      })
      break
    case 'HAS_GOLD':
      alertRef.value.hasGold = true
      alertRef.value.name = option.firstName
        if (option.cardType === 'VIRTUAL') {
          alertRef.value.getCard = true
        }
      sendAnalyticEvent('online_child_gold_rejected', {
        started_from: [store.getters['app/urlParams'].startedFrom],
        stage: ['start'],
        page_path: ['/bank/bpm/products/goldkids/goldkid'],
        reject_reason: [translate('alert:has-gold', 'name')],
        external_message_id: [store.getters['goldkidProcess/pId']]
      })
      break
    case 'MAX_AGE':
      alertRef.value.maxAge = true
      alertRef.value.name = option.firstName
      sendAnalyticEvent('online_child_gold_rejected', {
        started_from: [store.getters['app/urlParams'].startedFrom],
        stage: ['start'],
        page_path: ['/bank/bpm/products/goldkids/goldkid'],
        reject_reason: [translate('alert:max-age', 'name')],
        external_message_id: [store.getters['goldkidProcess/pId']]
      })
      break
    case 'MIN_AGE':
      alertRef.value.minAge = true
      alertRef.value.name = option.firstName
      sendAnalyticEvent('online_child_gold_rejected', {
        started_from: [store.getters['app/urlParams'].startedFrom],
        stage: ['start'],
        page_path: ['/bank/bpm/products/goldkids/goldkid'],
        reject_reason: [translate('alert:min-age', 'name')],
        external_message_id: [store.getters['goldkidProcess/pId']]
      })
      break
    case 'HAS_GOLD_WITH_REISSUE':
      alertRef.value.reissue = true
      alertRef.value.name = option.firstName
      sendAnalyticEvent('online_child_gold_rejected', {
        started_from: [store.getters['app/urlParams'].startedFrom],
        stage: ['start'],
        page_path: ['/bank/bpm/products/goldkids/goldkid'],
        reject_reason: [translate('alert:has-gold', 'name')],
        external_message_id: [store.getters['goldkidProcess/pId']]
      })
      break
  }
}

const goToMain = () => {
  window.location.href = process.env.VUE_APP_BASE_URL
}

onBeforeUnmount(() => {
  sendAnalyticEvent('online_child_gold_children_list', {
    started_from: [store.getters['app/urlParams'].startedFrom],
    stage: ['finish'],
    page_path: ['/bank/bpm/products/goldkids/goldkid'],
    children_total: [kidsList.value.length],
    children_available: [kidsList.value?.filter(item => item.status === 'OK').length],
    external_message_id: [store.getters['goldkidProcess/pId']]
  })
})

</script>
